import React, { useEffect, useRef } from "react";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { handleDropDowns } from "../../config/dropDownHelpers";

const UnlabelStockItemForm = ({
  control,
  fields = [],
  register,
  errors,
  watch,
  firstFieldRef,
  setValue,
  basePath = "stock", // single entry path
}) => {
  // masters from store
  const categories = useSelector((s) => s.categories?.categories);
  const products = useSelector((s) => s.products?.products);
  const vendors = useSelector((s) => s.vendors?.vendors);
  const designs = useSelector((s) => s.designs?.designs);
  const purities = useSelector((s) => s.purities?.purities);
  const branches = useSelector((s) => s.branches?.branches);

  // watch dependent dropdowns + weights
  const category = watch(`${basePath}.category`);
  const product = watch(`${basePath}.product`);
  const totalWt = watch(`${basePath}.totalWt`);
  const packingWt = watch(`${basePath}.packingWt`);
  const stoneWt = watch(`${basePath}.stoneWt`);
  const diamondWt = watch(`${basePath}.diamondWt`);

  // watch for fine/wastage calc
  const netWtVal = watch(`${basePath}.netWt`);
  const finePercentage = watch(`${basePath}.finePercentage`);
  const wastagePercentage = watch(`${basePath}.wastagePercentage`);

  // helper: set only when value actually changes (prevents loops)
  const setIfChanged = (name, next) => {
    const curr = watch(name);
    if (String(curr ?? "") !== String(next ?? "")) {
      setValue(name, next, { shouldDirty: true, shouldValidate: false });
    }
  };

  // derived weights: GrossWt & NetWt
  useEffect(() => {
    const tw = +totalWt || 0;
    const pk = +packingWt || 0;
    const st = +stoneWt || 0;
    const dm = (+diamondWt || 0) / 5;

    const gross = (tw - pk).toFixed(3);
    const net = (tw - pk - st - dm).toFixed(3);

    setIfChanged(`${basePath}.grossWt`, gross);
    setIfChanged(`${basePath}.netWt`, net);
  }, [totalWt, packingWt, stoneWt, diamondWt, setValue, basePath]); // watch/read inside setIfChanged

  // derived F + W Wt = NetWt * (Fine% + Wastage%) / 100
  useEffect(() => {
    const nw = parseFloat(netWtVal) || 0;
    const f = parseFloat(finePercentage) || 0;
    const w = parseFloat(wastagePercentage) || 0;

    const result = (nw * (f + w)) / 100;
    setIfChanged(
      `${basePath}.fineWastageWt`,
      Number.isFinite(result) ? result.toFixed(3) : ""
    );
  }, [netWtVal, finePercentage, wastagePercentage, setValue, basePath]);

  const cardRef = useRef(null);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="px-2 py-4 border border-gray-200 hover:border-gray-300 rounded-lg bg-white"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800">Unlabelled Stock</h3>
        <span className="text-[11px] text-slate-500">Single entry</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
        {fields?.map((item, idx) => {
          const colSpan = ["category", "product", "design", "description", "purity"].includes(
            item?.name
          )
            ? "md:col-span-2"
            : "md:col-span-1";

          const path = `${basePath}.${item.name}`;
          const rules = { required: false };

          // capture register once and reuse its ref for non-dropdown
          const reg = item?.type !== "dropdown" ? register(path, rules) : null;

          return (
            <div key={item.name || idx} className={colSpan}>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                {item?.label} {item?.mandatory && <span className="text-red-500">*</span>}
              </label>

              {/* TEXT */}
              {item?.type === "text" && (
                <>
                  <input
                    {...reg}
                    ref={(el) => {
                      reg?.ref?.(el);
                      if (idx === 0 && firstFieldRef) firstFieldRef.current = el;
                    }}
                    disabled={
                      item.name === "grossWt" ||
                      item.name === "netWt" ||
                      item.name === "fineWastageWt"
                    }
                    placeholder={item?.placeholder || ""}
                    className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                  />
                  {errors?.stock?.[item.name] && (
                    <p className="text-red-500 text-xs mt-1">{errors.stock[item.name].message}</p>
                  )}
                </>
              )}

              {/* NUMBER */}
              {item?.type === "number" && (
                <>
                  <input
                    type="number"
                    {...reg}
                    ref={(el) => {
                      reg?.ref?.(el);
                      if (idx === 0 && firstFieldRef) firstFieldRef.current = el;
                    }}
                    placeholder={item?.placeholder || ""}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                  />
                  {errors?.stock?.[item.name] && (
                    <p className="text-red-500 text-xs mt-1">{errors.stock[item.name].message}</p>
                  )}
                </>
              )}

              {/* DATE */}
              {item?.type === "date" && (
                <>
                  <input
                    type="date"
                    {...reg}
                    ref={(el) => {
                      reg?.ref?.(el);
                      if (idx === 0 && firstFieldRef) firstFieldRef.current = el;
                    }}
                    className="w-full px-3 h-8 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                  />
                  {errors?.stock?.[item.name] && (
                    <p className="text-red-500 text-xs mt-1">{errors.stock[item.name].message}</p>
                  )}
                </>
              )}

              {/* DROPDOWN */}
              {item?.type === "dropdown" && (
                <>
                  <Controller
                    name={path}
                    control={control}
                    rules={rules}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={handleDropDowns({
                          key: item.name,
                          vendors,
                          categories,
                          products,
                          designs,
                          purities,
                          branches,
                          category,
                          product,
                        })}
                        classNamePrefix="react-select"
                        menuPlacement="bottom"
                        isClearable
                        isSearchable
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "32px",
                            height: "32px",
                            borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
                            boxShadow: state.isFocused
                              ? "0 0 0 2px rgba(167,139,250,0.35)"
                              : "none",
                            fontSize: "12px",
                            backgroundColor: "white",
                          }),
                          indicatorsContainer: (b) => ({ ...b, height: "30px" }),
                          dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
                          clearIndicator: (b) => ({ ...b, padding: "4px" }),
                          valueContainer: (b) => ({ ...b, padding: "0 6px", height: "30px" }),
                          input: (b) => ({ ...b, margin: 0, padding: 0 }),
                          menu: (b) => ({ ...b, fontSize: "12px" }),
                          menuList: (b) => ({ ...b, maxHeight: "120px", overflowY: "auto" }),
                          singleValue: (b) => ({ ...b, fontSize: "12px" }),
                        }}
                        placeholder={`Select ${item.label}`}
                      />
                    )}
                  />
                  {errors?.stock?.[item.name] && (
                    <p className="text-red-500 text-xs mt-1">{errors.stock[item.name].message}</p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default UnlabelStockItemForm;
